import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Lightbulb,
  PencilLine,
  QuestionMark,
  Spinner,
  UsersThree,
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  ActivityLogTab,
  BatchesAndUsers,
  Collaborators,
  NotesTab,
  PartnerInfo,
  SelfPacedCourseLandingPage,
  SelfPacedCourseSessions,
} from "@/components";
import { coursesQueryKeys, useCourses, usePartners } from "@/hooks";
import { MODAL_ROUTES } from "@/router";
import { useNavigateModal } from "@/router/useNavigateModal";
import { useUserStore } from "@/stores";
import type { Partner } from "@/types";
import type { Tab } from "@/ui";
import {
  Button,
  Circle,
  errorToast,
  Heading,
  Loader,
  Modal,
  Tabs,
  Toggle,
  useToastStore,
} from "@/ui";
import { tw } from "@/utils";

const PUBLISH_OPTIONS: [string, string] = ["Draft", "Published"];

const Field = ({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={tw("flex flex-col gap-1", wide && "sm:col-span-2 lg:col-span-3")}
  >
    <span className="text-xs font-medium uppercase tracking-wide text-neutral-400">
      {label}
    </span>
    <span className="text-sm text-neutral-900">{children}</span>
  </div>
);

const OverviewView = ({
  partner,
  partnerId,
}: {
  partner: Partner | undefined;
  partnerId: string | undefined;
}) => {
  const { t } = useTranslation();
  const [notesOpen, setNotesOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  return (
    <div className="flex flex-col gap-y-8 pt-6">
      {/* Partner info grid */}
      <div className="grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t("partner.main_contact")}>
          {partner?.contact ?? "—"}
        </Field>

        <Field label={t("partner.type_of_partner")}>
          <span className="capitalize">{partner?.type}</span>
        </Field>

        <Field label={t("partner.country")}>{partner?.country ?? "—"}</Field>

        {partner?.domain ? (
          <Field label={t("partner.domain")}>{partner.domain}</Field>
        ) : null}

        <Field label={t("partner.description")} wide>
          {partner?.description ?? "—"}
        </Field>
      </div>

      {/* Notes accordion */}
      {partnerId && (
        <div className="border-t border-neutral-200">
          <button
            type="button"
            onClick={() => setNotesOpen((o) => !o)}
            className="flex w-full items-center justify-between py-4 text-left"
          >
            <span className="text-sm font-semibold text-neutral-700">
              Notes
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`h-4 w-4 text-neutral-400 transition-transform duration-200 ${notesOpen ? "rotate-180" : ""}`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m19.5 8.25-7.5 7.5-7.5-7.5"
              />
            </svg>
          </button>
          {notesOpen && (
            <div className="pb-6">
              <NotesTab
                endpoint={`/partners/${partnerId}/notes`}
                queryKey={["notes", "partner", partnerId]}
              />
            </div>
          )}
        </div>
      )}

      {/* Activity Log accordion */}
      {partnerId && (
        <div className="border-t border-neutral-200">
          <button
            type="button"
            onClick={() => setActivityOpen((o) => !o)}
            className="flex w-full items-center justify-between py-4 text-left"
          >
            <span className="text-sm font-semibold text-neutral-700">
              Activity Log
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`h-4 w-4 text-neutral-400 transition-transform duration-200 ${activityOpen ? "rotate-180" : ""}`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m19.5 8.25-7.5 7.5-7.5-7.5"
              />
            </svg>
          </button>
          {activityOpen && (
            <div className="pb-6">
              <ActivityLogTab subjectType="partner" subjectId={partnerId} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const PartnerDetail = () => {
  const { partnerId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navigateModal = useNavigateModal();
  const user = useUserStore((state) => state.user);
  const queryClient = useQueryClient();
  const pushToast = useToastStore((state) => state.pushToast);

  const { usePartner, useSelfPacedCourse } = usePartners();
  const { usePublishCourse } = useCourses();

  const { data: partner, isLoading: isLoadingPartner } = usePartner(partnerId);
  const { data: course, isLoading: isLoadingCourse } = useSelfPacedCourse(
    partnerId,
    {
      refetchOnWindowFocus: false,
    },
  );

  const { mutateAsync: updatePublishStatus, isPending: isPublishing } =
    usePublishCourse();
  const [selectedOption, setSelectedOption] = useState(PUBLISH_OPTIONS[0]);
  const [pendingPublish, setPendingPublish] = useState<string | null>(null);
  const [currentTabLabel, setCurrentTabLabel] = useState<string>("Overview");

  useEffect(() => {
    if (course?.published === true) {
      setSelectedOption(PUBLISH_OPTIONS[1]);
    }
  }, [course]);

  const requestToggle = (option: string) => {
    if (option !== selectedOption) {
      setPendingPublish(option);
    }
  };

  const applyToggle = async (option: string) => {
    setSelectedOption(option);

    try {
      await updatePublishStatus(course?.id);
      void queryClient.invalidateQueries({
        queryKey: coursesQueryKeys.useCourse(course?.id),
        exact: true,
      });

      if (option === "Published") {
        void pushToast({
          type: "success",
          title: t("course.published"),
          message: t("course.published_successfully_message"),
        });
      } else {
        void pushToast({
          type: "success",
          title: t("course.draft"),
          message: t("course.draft_successfully_message"),
        });
      }
    } catch (error) {
      errorToast(error);
      setSelectedOption(
        option === PUBLISH_OPTIONS[0] ? PUBLISH_OPTIONS[1] : PUBLISH_OPTIONS[0],
      );
    }
  };

  const confirmToggle = async () => {
    const option = pendingPublish;
    if (!option) {
      return;
    }
    await applyToggle(option);
    setPendingPublish(null);
  };

  if (isLoadingPartner)
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );

  if (!partner && !isLoadingPartner)
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Heading level={2} heading="Partner not found" />
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go back
        </Button>
      </div>
    );

  const tabs: Tab[] = [
    {
      label: "Overview",
      component: <OverviewView partner={partner} partnerId={partnerId} />,
      roles: ["admin"],
    },
    {
      label: t("course.batches_and_users"),
      component: <BatchesAndUsers />,
      roles: ["admin"],
      icon: <UsersThree />,
    },
    {
      label: t("course.self_paces_course_landing_page"),
      component: (
        <SelfPacedCourseLandingPage partnerId={partner?.id} course={course} />
      ),
      roles: ["admin", "partner"],
      icon: <PencilLine />,
    },
    {
      label: t("course.self_paces_course_sessions"),
      component: (
        <SelfPacedCourseSessions partnerId={partner?.id} course={course} />
      ),
      roles: ["admin", "partner"],
      icon: <PencilLine />,
    },
    {
      label: t("course.collaborators"),
      component: <Collaborators partnerId={partner?.id} course={course} />,
      roles: ["admin", "partner"],
      icon: <PencilLine />,
    },
    {
      label: t("course.info"),
      component: <PartnerInfo partner={partner} />,
      roles: ["admin"],
      icon: <Lightbulb />,
    },
  ];

  const availableTabs = tabs.filter((tab) =>
    tab.roles.includes(user?.role ?? "admin"),
  );

  return (
    <div className="flex h-full flex-col gap-6 p-4 sm:p-6 lg:p-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex w-fit items-center gap-2 text-sm font-medium text-neutral-400 hover:text-primary-700"
      >
        <ArrowLeft size={18} />
        Back
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          {partner?.logo ? (
            <img
              src={partner.logo}
              alt={partner.name}
              className="size-12 flex-none rounded-full object-cover"
            />
          ) : (
            <Circle className="size-12 flex-none bg-neutral-100">
              <QuestionMark size={22} className="text-neutral-400" />
            </Circle>
          )}
          <div className="min-w-0">
            <Heading level={1} heading={partner?.name} />
            <p className="mt-1 text-sm capitalize text-neutral-500">
              {partner?.type}
              {partner?.country ? ` · ${partner.country}` : ""}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {course && user?.role === "admin" && (
            <Toggle
              options={PUBLISH_OPTIONS}
              selectedOption={selectedOption}
              onToggle={requestToggle}
            />
          )}
          <Button
            size="sm"
            onClick={() => navigateModal(MODAL_ROUTES.editPartner, { partner })}
          >
            <PencilLine size={16} />
            Edit
          </Button>
        </div>
      </div>

      {isLoadingCourse ? (
        <Loader />
      ) : (
        <Tabs
          tabs={availableTabs}
          userRole={user?.role ?? "admin"}
          currentLabel={currentTabLabel}
          setCurrentLabel={setCurrentTabLabel}
        />
      )}

      <Modal
        show={pendingPublish !== null}
        onClose={() => setPendingPublish(null)}
        title={
          pendingPublish === "Published"
            ? "Publish course?"
            : "Move course to draft?"
        }
        className="w-full sm:w-[440px]"
      >
        <div className="flex flex-col gap-y-6">
          <p className="text-sm text-neutral-500">
            {pendingPublish === "Published"
              ? "This makes the self-paced course visible to this partner's users."
              : "This hides the self-paced course from this partner's users."}
          </p>

          <div className="flex justify-center gap-2">
            <Button
              variant="outlined"
              className="flex-grow"
              disabled={isPublishing}
              onClick={() => setPendingPublish(null)}
            >
              {t("general.cancel")}
            </Button>
            <Button
              className="flex-grow"
              disabled={isPublishing}
              onClick={() => void confirmToggle()}
            >
              {isPublishing ? (
                <Spinner className="h-5 w-5 animate-spin" />
              ) : pendingPublish === "Published" ? (
                "Publish"
              ) : (
                "Move to draft"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
